import {
  AvatarGroup,
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  IconButton,
  Skeleton,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { useState } from 'react'
import { TbCheck, TbDots } from 'react-icons/tb'
import { useNavigate } from 'react-router-dom'
import { pushAndNavigate } from '../../native/SwipeBack'
import { patchEventCheckIn } from '../../api/user/play/Play'
import { getMapsUrl } from '../../pages/user/PlayExpand'
import { ExclamationTriangle, MessagesFilled } from '../icons/icons'
import LazyAvatar from '../lazy/LazyAvatar'
import { LazyLoadedImage } from '../lazy/LazyLoadedImage'
import UserSelectModal from '../modals/UserSelectModal'
import { getCourtName, type CourtValue } from '../../functions/CourtName'

interface EventUser {
  id: string
  name: string
  avatar: string
  checked_in?: boolean
  follows?: boolean | null
  is_child?: boolean
  attribution?: string
  // EventBooking id — required for the check-in checkbox to render and to target
  // the right booking (self vs each kid). UserSelectModal keys check-in by this.
  booking_id?: string
}

interface EventJoinedCardProps {
  host: EventUser
  coach: EventUser[]
  players: EventUser[]
  title: string
  time: Date
  type: string
  secondary_type?: string
  court: CourtValue
  level: string
  location: string
  address?: string
  duration: number
  isCancelled: boolean
  isCollapsed?: boolean
  cancellationReason?: string
  thumbnail: string
  isSwitchable?: boolean
  status?: string

  isHostOrCoach?: boolean
  eventId?: string

  onExtraOptionsOpen: () => void
  onSwitchTime: () => void
  onSwitchSelect: () => void
  onCancel: () => void

  isPast?: boolean
  isTablet: boolean
  isLoading?: boolean
  width?: string
  isSkeleton?: boolean

  // When provided, clicking the Players row delegates to the parent instead
  // of opening the card-local modal. The parent owns the modal so its state
  // can survive navigation (e.g. back from a player's profile re-opens it).
  onPlayersClick?: (eventId: string) => void
}

const formatDateTime = (date: Date): string => {
  const today = new Date()
  const tomorrow = new Date(today)

  const isToday = date.toDateString() === today.toDateString()
  const isTomorrow = date.toDateString() === tomorrow.toDateString()
  const longOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: 'numeric',
  }

  const shortOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
  }

  tomorrow.setDate(today.getDate() + 1)
  tomorrow.setHours(12, 30, 0, 0)

  if (isToday) {
    return `Today, ${date.toLocaleTimeString('en-US', shortOptions)}`
  } else if (isTomorrow) {
    return `Tomorrow, ${date.toLocaleTimeString('en-US', shortOptions)}`
  } else {
    return date.toLocaleDateString('en-US', longOptions)
  }
}

const EventJoinedCard = (props: EventJoinedCardProps) => {
  const { isOpen: isCoachOpen, onOpen: onCoachOpen, onClose: onCoachClose } = useDisclosure()

  const { isOpen: isPlayerOpen, onClose: onPlayerClose, onOpen: onPlayerOpen } = useDisclosure()

  const navigate = useNavigate()

  const [checkedInIds, setCheckedInIds] = useState<Set<string>>(
    () => new Set(props.players.filter((p) => p.checked_in && p.booking_id).map((p) => p.booking_id as string)),
  )

  // `bookingId` is the EventBooking id (UserSelectModal passes user.booking_id).
  // Check in by booking_id, not member_id: a member holds a booking per attendee
  // (self + each kid), and patching member_id would 404 on kid rows.
  const handleCheckIn = async (bookingId: string, checked: boolean) => {
    if (!props.eventId) return
    setCheckedInIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(bookingId)
      else next.delete(bookingId)
      return next
    })
    try {
      await patchEventCheckIn({ event_id: props.eventId, booking_id: bookingId, checked_in: checked })
    } catch (e) {
      console.error('Check-in failed', e)
      setCheckedInIds((prev) => {
        const next = new Set(prev)
        if (checked) next.delete(bookingId)
        else next.add(bookingId)
        return next
      })
    }
  }

  const formatDuration = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    if (hours === 1 && minutes === 0) {
      return '1 hour'
    } else if (hours === 2 && minutes === 0) {
      return '2 hours'
    } else if (hours === 3 && minutes === 0) {
      return '3 hours'
    }

    return `${hours > 0 ? `${hours} hr ` : ''}${minutes > 0 ? `${minutes} min` : ''}`.trim()
  }

  return (
    <>
      <UserSelectModal
        isOpen={isPlayerOpen}
        onClose={onPlayerClose}
        isAdmin={false}
        users={props.players}
        onChange={() => {}}
        title="All players"
        hideButtons={true}
        customIcon={
          <Icon w="20px" h="20px" viewBox="0 0 24 24">
            <MessagesFilled />
          </Icon>
        }
        enableCheckIn={props.isHostOrCoach}
        checkedInIds={checkedInIds}
        onCheckIn={handleCheckIn}
      />
      <UserSelectModal
        isOpen={isCoachOpen}
        onClose={onCoachClose}
        isAdmin={false}
        users={props.coach}
        onChange={() => {}}
        title="All coaches"
        hideButtons={true}
        customIcon={
          <Icon w="20px" h="20px" viewBox="0 0 24 24">
            <MessagesFilled />
          </Icon>
        }
      />

      <Skeleton
        isLoaded={!props.isSkeleton}
        borderRadius="10px"
        mb={props.isSkeleton ? '20px' : ''}
        p={props.isSkeleton ? '20px' : ''}
        w={props.width || (props.isTablet || !props.isSwitchable ? '100%' : '50%')}
      >
        <Box
          bg="white"
          borderRadius="10px"
          boxShadow="0px 12.914606094360352px 86.71235656738281px 0px #2B2C2D14"
          w="100%"
          maxW="100%"
          minW="100%"
          borderWidth="1px"
          borderColor="#DCE2EA"
          overflow="hidden"
          pos="relative"
        >
          {!props.isSwitchable && (
            <IconButton
              right="5px"
              top="5px"
              variant="unstyled"
              icon={<Icon as={TbDots} w="22px" h="22px" />}
              position="absolute"
              aria-label="Options"
              onClick={() => props.onExtraOptionsOpen()}
            />
          )}

          <Box
            p={
              props.isCollapsed
                ? props.isTablet
                  ? '15px'
                  : '20px'
                : props.isTablet
                  ? '15px 15px 0 15px'
                  : '20px 20px 0 20px'
            }
          >
            <Flex gap="0px">
              <LazyLoadedImage
                w="calc(50% - 20px)"
                height={(!props.isTablet ? 152 : 120) + (props.isSwitchable ? 0 : 20)}
                src={props.thumbnail}
                borderRadius="10px"
                mr="20px"
              />
              <Box w="50%">
                {!props.isCancelled && !props.isSwitchable && (
                  <Flex gap="2px" padding="2px 10px" borderRadius="50px" bg={props.isPast ? '#171C22' : '#22C55E'} w="fit-content">
                    <Text fontSize="sm" color="white">
                      {props.isPast ? 'Completed' : 'Going'}
                    </Text>
                    {!props.isPast && <Icon as={TbCheck} color="white"></Icon>}
                  </Flex>
                )}
                <Text variant="title" fontSize={props.isTablet ? 'lg' : undefined}>
                  {props.title}
                </Text>
                <Text
                  variant="subtle"
                  fontSize="xs"
                  mt={props.isTablet ? '6px' : '5px'}
                  mb={props.isTablet ? '10px' : '20px'}
                  cursor="pointer"
                  onClick={() => pushAndNavigate(navigate, `/user/profile?id=${props.host.id}`)}
                >
                  By host <span style={{ color: '#171C22' }}>{props.host.name}</span>
                </Text>
                <Flex
                  alignItems="center"
                  gap="4px"
                  mb={props.isTablet ? '14px' : '20px'}
                  onClick={() => onCoachOpen()}
                  cursor={props.coach.length > 0 ? 'pointer' : 'default'}
                >
                  <Text variant="header" fontSize={props.isTablet ? '13px' : '15px'}>
                    Coach({props.coach.length}):
                  </Text>
                  {props.coach.length > 0 && (
                    <AvatarGroup overflow="hidden">
                      {props.coach.map((player: any, index: number) => {
                        return (
                          <>
                            <LazyAvatar
                              position="relative"
                              src={player.avatar ?? ''}
                              key={player.id}
                              width={26}
                              height={26}
                              border="1px solid #dce2ea"
                              zIndex={props.coach.length - index}
                              bg="white"
                              right={index != 0 ? `${10 + (index - 1) * 26 - 16 * (index - 1)}px` : '0px'}
                              //onClick={() => navigate(`/user/profile?id=${player.id}`)}
                            />
                          </>
                        )
                      })}
                    </AvatarGroup>
                  )}
                  {props.coach.length == 0 && (
                    <Text variant="subtle" fontSize={props.isTablet ? '11px' : '13px'} fontWeight="400" mt="2px">
                      No coach
                    </Text>
                  )}
                  {/* <AvatarGroup>
                            {props.coach.map((coach) => (
                                <Avatar 
                                    src={coach.avatar}
                                    key={coach.id} 
                                    w={props.isTablet ? '20px' : '26px'} h={props.isTablet ? '20px' : '26px'}
                                    border='1px solid #dce2ea' 
                                />
                            ))
                            }
                        </AvatarGroup> */}
                </Flex>
                <Flex
                  alignItems="center"
                  gap="4px"
                  onClick={() => {
                    if (props.onPlayersClick && props.eventId) {
                      props.onPlayersClick(props.eventId)
                    } else {
                      onPlayerOpen()
                    }
                  }}
                  cursor={props.players.length > 0 ? 'pointer' : 'default'}
                >
                  <Text variant="header" fontSize={props.isTablet ? '13px' : '15px'}>
                    Players({props.players.length}):
                  </Text>
                  <AvatarGroup overflow="hidden">
                    {props.players.slice(0, props.isTablet ? 3 : 4).map((player: any, index: number) => {
                      const maxAvatars = props.isTablet ? 3 : 4
                      return (
                        <>
                          <LazyAvatar
                            position="relative"
                            src={player.avatar ?? ''}
                            key={player.id}
                            width={26}
                            height={26}
                            border="1px solid #dce2ea"
                            zIndex={Math.min(maxAvatars, props.players.length) - index}
                            bg="white"
                            right={index != 0 ? `${10 + (index - 1) * 26 - 16 * (index - 1)}px` : '0px'}
                            //onClick={() => navigate(`/user/profile?id=${player.id}`)}
                          />
                        </>
                      )
                    })}
                    {props.players.length > (props.isTablet ? 3 : 4) && (
                      <Box
                        position="relative"
                        width={26}
                        height={26}
                        minWidth={26}
                        minHeight={26}
                        borderRadius="50%"
                        bg="#F5F7F9"
                        border="1px solid #dce2ea"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        right={props.isTablet ? `${10 + 2 * 26 - 16 * 2 - 8}px` : `${10 + 3 * 26 - 16 * 3 - 8}px`}
                        zIndex={0}
                        flexShrink={0}
                      >
                        <Text fontSize="10px" fontWeight="600" color="#171C22">
                          +{props.players.length - (props.isTablet ? 3 : 4)}
                        </Text>
                      </Box>
                    )}
                  </AvatarGroup>
                  {/* <AvatarGroup>
                            {props.players.map((players) => (
                                <Avatar 
                                    src={players.avatar}
                                    key={players.id}
                                    w={props.isTablet ? '20px' : '26px'} h={props.isTablet ? '20px' : '26px'}
                                    border='1px solid #dce2ea' 
                                />
                            ))}
                        </AvatarGroup> */}
                </Flex>
              </Box>
            </Flex>
          </Box>

          {!props.isCollapsed && (
            <Box p={props.isTablet ? '15px' : '20px'}>
              <Flex mb={props.isTablet ? '5px' : '12px'}>
                <Flex alignItems="center" gap="4px" w="50%">
                  <Text variant="header" fontSize={props.isTablet ? '12' : '15'}>
                    Time:
                  </Text>
                  <Text variant="subtle" fontSize={props.isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                    {formatDateTime(props.time)}
                  </Text>
                </Flex>

                <Flex alignItems="center" gap="4px">
                  <Text variant="header" fontSize={props.isTablet ? '12' : '15'}>
                    Type:
                  </Text>
                  <Text variant="subtle" fontSize={props.isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                    {props.type}
                  </Text>
                </Flex>
                {props.secondary_type && (
                  <Flex alignItems="center" gap="4px">
                    <Text variant="header" fontSize={props.isTablet ? '12' : '15'}>
                      Secondary Type:
                    </Text>
                    <Text variant="subtle" fontSize={props.isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                      {props.secondary_type}
                    </Text>
                  </Flex>
                )}
              </Flex>
              <Flex mb={props.isTablet ? '5px' : '12px'}>
                <Flex alignItems="center" gap="4px" w="50%">
                  <Text variant="header" fontSize={props.isTablet ? '12' : '15'}>
                    Court:
                  </Text>
                  <Text variant="subtle" fontSize={props.isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                    {getCourtName(props.court)}
                  </Text>
                </Flex>

                <Flex alignItems="center" gap="4px">
                  <Text variant="header" fontSize={props.isTablet ? '12' : '15'}>
                    Level:
                  </Text>
                  <Text variant="subtle" fontSize={props.isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                    {props.level}
                  </Text>
                </Flex>
              </Flex>
              <Flex alignItems="center" gap="4px" mb={props.isTablet ? '5px' : '12px'}>
                <Text variant="header" fontSize={props.isTablet ? '12' : '15'}>
                  Location:
                </Text>
                <Text variant="subtle" fontSize={props.isTablet ? '11' : '13'} fontWeight="400" mt="2px">
                  {props.location}
                </Text>
              </Flex>
              <Flex alignItems="center" gap="4px">
                <Text variant="header" fontSize={props.isTablet ? '12' : '15'}>
                  Address:
                </Text>
                <Text
                  variant="subtle"
                  fontSize={props.isTablet ? '11' : '13'}
                  fontWeight="400"
                  mt="2px"
                  cursor="pointer"
                  textDecoration="underline"
                  onClick={() => props.address && window.open(getMapsUrl(props.address))}
                >
                  {props.address}
                </Text>
              </Flex>
            </Box>
          )}
          <Divider />
          <Flex
            p="20px"
            alignItems="center"
            gap="10px"
            justifyContent={props.isCancelled && props.cancellationReason ? 'flex-start' : 'space-between'}
          >
            {props.isCancelled && props.cancellationReason ? (
              <>
                <Icon w="20px" h="20px" viewBox="0 0 20 20" fill="none" color="#FF3B30">
                  <ExclamationTriangle />
                </Icon>
                <Text color="#171C22" fontSize="md">
                  {props.cancellationReason}
                </Text>
              </>
            ) : (
              <>
                <Text fontWeight="600" color="#171C22" fontSize="lg">
                  {formatDuration(Number(props.duration))}
                </Text>
                <Flex gap="10px">
                  {props.isCancelled && (
                    <Button
                      variant="heavy"
                      isDisabled
                      fontWeight={!props.isTablet ? '600' : undefined}
                      size={props.isTablet ? 'sm' : undefined}
                    >
                      Cancelled
                    </Button>
                  )}

                  {!props.isCancelled && !props.isSwitchable && !props.isPast && props.status !== 'booked_as_coach' && (
                    <>
                      <Button
                        variant="light"
                        fontWeight={!props.isTablet ? '600' : undefined}
                        size={props.isTablet ? 'sm' : undefined}
                        onClick={() => props.onSwitchTime()}
                      >
                        Switch Event
                      </Button>
                      <Button
                        variant="light"
                        fontWeight={!props.isTablet ? '600' : undefined}
                        size={props.isTablet ? 'sm' : undefined}
                        onClick={() => props.onCancel()}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  {props.isSwitchable && (
                    <>
                      <Button
                        variant="heavy"
                        fontWeight={!props.isTablet ? '600' : undefined}
                        size={props.isTablet ? 'sm' : undefined}
                        onClick={() => props.onSwitchSelect()}
                        isLoading={props.isLoading}
                      >
                        Switch to this Event
                      </Button>
                    </>
                  )}
                </Flex>
              </>
            )}
          </Flex>
        </Box>
      </Skeleton>
    </>
  )
}

export default EventJoinedCard
